import { getAppUrl } from "@lyder-talk/core";
import { hasSessionHint } from "@lyder-talk/core/lib/session-hint";

/**
 * Send a signed-in visitor from the landing page into the app.
 *
 * The session itself lives in `localStorage` on the app subdomain, which this
 * origin cannot read — so the app publishes a hint cookie instead
 * (`packages/core/src/lib/session-hint.ts`). Someone who is already signed in
 * has no use for the marketing page: they came to study, so the first thing they
 * see should be their desk.
 *
 * Called from `main.tsx` **before React mounts**, not from an effect, so there is
 * no flash of the hero before the navigation. `replace` rather than `assign`
 * keeps the redirect out of history — pressing Back should not land on a page
 * that immediately forwards again.
 *
 * @returns true when a navigation was started, so the caller can skip mounting.
 */
export const redirectSignedInVisitor = (): boolean => {
  if (typeof window === "undefined") return false;

  // The landing page only. The blog, the updates feed and the legal pages are
  // content a signed-in visitor may legitimately want to read.
  const path = window.location.pathname.replace(/\/index\.html$/, "/");
  if (path !== "/") return false;

  // An invite / recovery / confirmation link carries a token only the app can
  // consume; forwarding to the app's root would throw that token away.
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const authFlow =
    hash.get("type") || search.get("type") || hash.get("access_token") || search.get("access_token");
  if (authFlow) return false;

  if (!hasSessionHint()) return false;

  window.location.replace(getAppUrl("/"));
  return true;
};
